import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { debounce } from 'lodash-es';
import { Icon, Layout, Justify, Segment } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import Loading from '@intelligence/components/Loading';
import SortBySelector from './components/SortBySelector';
// import SortBySelector, { OrderTypeMap } from '@intelligence/pages/InsightsReport/components/SortBySelector';
import ReportCard from '../../components/ReportCard/InsightsReportCard';
import SearchBar from './SearchBar';
import useData from './_hooks';
import { buriedPointPage, buriedPointGame } from '@home/utils/buriedPoint';
// import { sortReportsData } from '@intelligence/pages/InsightsReport/_utils';
import { IS_WOA } from '@home/_config/config';
import { ReportPageType } from './_config';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import '@intelligence/components/LazyReport/index.scss';
import './index.scss';

const { Content } = Layout;

const NEW_MARK_IDS_MAPPING_KEY = 'ReportsAIHubReports';

interface IHandleCountsDataChangeProps {
  resourceId: string;
  fields: 'report_like' | 'watched_cnt';
  counts?: number;
}

export interface ReportProps {
  title: string;
  subTitle: string;
  segmentList?: any[];
  reportType: string;
  getReportFun: Function;
  searchType: string;
  pageLogMes: any; // TODO类型，用于搜索打点
  reportTypeConfig: ReportPageType;
  preClassName?: string;
}

export default function InsightsReport(props: ReportProps) {
  const { formatMessage } = useIntl();

  const {
    title,
    subTitle,
    segmentList = [],
    reportType,
    getReportFun,
    reportTypeConfig,
    preClassName = 'dbi-ai-hub-report',
  } = props;

  const {
    // segmentList,
    activeId,
    setActiveId,
    onSearch,
    handleSearchRecentClick,
    deleteSearchRecent,
    cacheRecentSearch,
    dataLoading,
    dataPartial,
    lang,
    search,
    onClear,
    shareBack,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    handleTabContent,
    mutate,
    orderType,
    setOrderType,
    languageSegmentList,
    segmentValue,
    onSegmentChange,
  } = useData(props);

  // console.log('insightsReport', props);
  const scrollBody = useRef<HTMLDivElement>(null);
  const [segmentFixed, setSegmentFixed] = useState(false);
  const [scrollPageCount, setScrollPageCount] = useState(0);

  const isStudioReport = reportType === 'studioReport';
  const isAIHubReport = reportType === 'aiHubReports';

  // 监听页面滚动 -- 吸顶
  useEffect(() => {
    if (scrollBody.current) {
      const handleScroll = debounce((e: any) => {
        const { scrollTop } = e.srcElement;
        // 添加scroll值变化
        setScrollPageCount((scrollPageCount) => scrollPageCount + 1);
        // setLikeCnt(scrollPageCount => scrollPageCount + 1)
        // 滚动判断距离 和定位条位置相关，开发调整
        if (isAIHubReport) {
          if (scrollTop > 260) {
            setSegmentFixed(true);
          } else {
            setSegmentFixed(false);
          }
        } else {
          setSegmentFixed(false);
        }
      }, 100);
      scrollBody.current?.addEventListener('scroll', handleScroll, false);
      return () => scrollBody.current?.removeEventListener('scroll', handleScroll, false);
    }
  }, [scrollBody]);

  const handleTabChange = (tab: any) => {
    setActiveId(tab.id);
    handleTabContent(tab.id);
    if (tab?.pagePoint) {
      if (isStudioReport) {
        buriedPointGame(tab?.pagePoint, tab?.pagePoint?.uid, tab?.pagePoint?.uidType);
      } else {
        buriedPointPage(tab?.pagePoint);
      }
    }
  };

  const onClickSearchRecent = (item: any) => {
    handleSearchRecentClick(item);
  };

  const getTabList = () => (
    <div className={`header-tab ${preClassName}-tab`}>
      <Tabs
        ceiling
        newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
        tabs={segmentList}
        animated={false}
        activeId={activeId}
        onActive={(tab) => {
          handleTabChange(tab);
        }}
      />
    </div>
  );

  // 点赞数/浏览数变化更新数据并排序
  const handleCountsDataChange = ({ resourceId, fields, counts }: IHandleCountsDataChangeProps) => {
    // 当前展示数据对应数据处理
    const newData: any = {
      ...data,
      list: data?.list?.map((item) => {
        if (resourceId === item?.resource_id) {
          const newCounts = Number(counts) > 0 ? counts : item?.[fields] + 1;
          return { ...item, [fields]: newCounts };
        }
        return item;
      }),
    };

    // 当前展示数据排序
    // [OrderTypeMap.likes, OrderTypeMap.views].includes(orderType) &&
    //   sortReportsData(newData.list, orderType);

    // 更新原始数据点赞/浏览数
    const currentReport: any = dataPartial?.find((item: any) => resourceId === item?.resource_id);
    const newCounts = Number(counts) > 0 ? counts : (currentReport?.[fields] || 0) + 1;
    currentReport && (currentReport[fields] = newCounts);

    mutate({ ...newData });
  };

  // 点赞量数据处理
  const handleLikeBtnClick = (resourceId: string, likeCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: likeCounts, fields: 'report_like' });
  };

  // 浏览量数据处理
  const handleViewsChange = (resourceId: string, viewCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: viewCounts, fields: 'watched_cnt' });
  };

  const handleSortByChange = (orderType: string) => {
    setOrderType(orderType);
  };

  return (
    <Content className={preClassName} ref={scrollBody}>
      <Content.Header
        className={classnames([
          'dbi-header contentHead-normal dbi-header-des',
          segmentList?.length ? `${preClassName}-header` : 'dbi-report-header',
        ])}
        title={title}
      >
        <div className="header-des">
          <span className="header-des-text">{subTitle}</span>
        </div>
        <SearchBar
          key={'reportSearchBar'}
          preClassName={preClassName}
          searchValue={search}
          onSearch={onSearch}
          onClear={onClear}
          cacheRecentSearch={cacheRecentSearch}
          onClickSearchRecent={onClickSearchRecent}
          deleteSearchRecent={deleteSearchRecent}
          scrollPageCount={scrollPageCount}
        />
        {segmentList?.length > 0 ? getTabList() : null}
      </Content.Header>

      <Content.Body full ref={lazyScrollRef} className={`${preClassName}-body-full`}>
        <MarqueeBanner />
        {/* 吸顶内容 */}
        <div className={`${preClassName}-header-fixed header-segment ${segmentFixed ? 'visible' : 'hide'}`}>
          {segmentFixed && (
            <>
              {/* <SearchBar
                key={'reportSearchBarFixed'}
                preClassName={preClassName}
                searchValue={search}
                onSearch={onSearch}
                onClear={onClear}
                cacheRecentSearch={cacheRecentSearch}
                onClickSearchRecent={onClickSearchRecent}
                deleteSearchRecent={deleteSearchRecent}
                scrollPageCount={scrollPageCount}
              /> */}
              {getTabList()}
            </>
          )}
        </div>

        <Justify
          className="dbi-reports-action-bar"
          left={
            segmentList?.length > 0 && (
              <Segment
                className="segment-light"
                options={languageSegmentList}
                value={segmentValue}
                onChange={onSegmentChange}
              />
            )
          }
          // 排序选项
          right={
            isAIHubReport && (
              <SortBySelector defaultValue={orderType} onChange={handleSortByChange} style={{ marginBottom: 20 }} />
            )
          }
        />

        {loading ? (
          <div
            style={
              isStudioReport
                ? { marginTop: 188, color: '#262832' }
                : { height: 'calc(100vh - 300px)', color: '#262832' }
            }
          >
            <Loading content={formatMessage({ id: 'common_loading' })} />
          </div>
        ) : (
          <div>
            {data?.list?.map((item) => (
              <ReportCard
                key={item.resource_id}
                pdfPreviewDownloadPage="pdfPreviewDownload"
                data={item}
                // systemId={'intelligence_insights'}
                lang={lang}
                reportLangSegment={segmentValue}
                // reportType={reportType}
                isNew={
                  // !!moment(item?.update_time || item?.time).isAfter(moment().subtract(1, 'month'))
                  !!moment(item?.create_time || item?.time).isAfter(moment().subtract(1, 'month'))
                }
                shareBack={shareBack}
                // @ts-ignore
                getReportFun={getReportFun}
                reportTypeConfig={reportTypeConfig}
                needPopover={true}
                onChange={handleLikeBtnClick}
                onViewsChange={handleViewsChange}
                // 除 woa 环境外，展示邮箱信息
                showContactInfo={!IS_WOA && isAIHubReport}
              />
            ))}
          </div>
        )}
        {loadingMore && (
          <div className="dbi-lazyreport-loading dbi-lazyreport-end">
            <Icon size="l" type="loading" />
            &nbsp;&nbsp;&nbsp;{formatMessage({ id: 'dbi_reports_loading_more' })}
          </div>
        )}

        {dataPartial.length <= 0 && noMore && !loading && !dataLoading && (
          <div className={`dbi-reportcard-nodata ${preClassName}-nodata`}>
            {formatMessage({ id: 'dbi_reports_search_na_data' })}
          </div>
        )}
      </Content.Body>
    </Content>
  );
}
